/**
 * Google Ad Manager rewarded ad for web via Google Publisher Tag (plan section 5). Google decides when the reward
 * is earned (display ads after 5 s in view, video ads when complete); we only listen for `rewardedSlotGranted`.
 * The caller must already have asked the user (opt-in disclosure) before calling this.
 */
import { loadScript } from "../scripts";

export type AdOutcome = "granted" | "dismissed" | "unavailable";

declare global {
  interface Window {
    googletag?: any;
  }
}

const GPT = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
const READY_TIMEOUT_MS = 15000;

/** Calls `onGranted` as soon as Google grants the reward; resolves when the ad closes (or never showed). */
export async function showRewardedAd(adUnitPath: string, onGranted: () => void): Promise<AdOutcome> {
  window.googletag = window.googletag || { cmd: [] };
  await loadScript(GPT);
  const googletag = window.googletag;
  return new Promise<AdOutcome>((resolve) => {
    googletag.cmd.push(() => {
      const slot = googletag.defineOutOfPageSlot(adUnitPath, googletag.enums.OutOfPageFormat.REWARDED);
      if (!slot) {
        resolve("unavailable"); // page or device does not support rewarded
        return;
      }
      slot.addService(googletag.pubads());
      const pubads = googletag.pubads();
      let granted = false;
      let ready = false;
      const finish = (outcome: AdOutcome) => {
        pubads.removeEventListener("rewardedSlotReady", onReady);
        pubads.removeEventListener("rewardedSlotGranted", onGrant);
        pubads.removeEventListener("rewardedSlotClosed", onClosed);
        pubads.removeEventListener("slotRenderEnded", onRender);
        googletag.destroySlots([slot]);
        resolve(outcome);
      };
      const onReady = (e: any) => {
        if (e.slot !== slot) return;
        ready = true;
        e.makeRewardedVisible();
      };
      const onGrant = (e: any) => {
        if (e.slot !== slot || granted) return;
        granted = true;
        onGranted();
      };
      const onClosed = (e: any) => e.slot === slot && finish(granted ? "granted" : "dismissed");
      const onRender = (e: any) => e.slot === slot && e.isEmpty && finish("unavailable");
      pubads.addEventListener("rewardedSlotReady", onReady);
      pubads.addEventListener("rewardedSlotGranted", onGrant);
      pubads.addEventListener("rewardedSlotClosed", onClosed);
      pubads.addEventListener("slotRenderEnded", onRender);
      googletag.enableServices();
      googletag.display(slot);
      window.setTimeout(() => !ready && finish("unavailable"), READY_TIMEOUT_MS);
    });
  });
}
